/**
 * WEB-636 — multi-tab auth-token coherence.
 *
 * The access token lives in a single shared `localStorage` key, so a login in a
 * new tab overwrites it for every tab. Without a reaction, the old tab keeps
 * sending a token that now belongs to a DIFFERENT user (403s / silent
 * wrong-account state). We react to the cross-tab `storage` event and reload the
 * stale tab so it re-bootstraps coherently with whatever token is now stored.
 *
 * We reload only when the token's user (`sub`) actually changes — a same-user
 * token refresh (written by the API layer) must not reload every other tab.
 */

/** Decode the `sub` claim from a JWT without verifying its signature. */
function decodeSub(token: string | null): string | null {
  if (!token) return null;
  const part = token.split('.')[1];
  if (!part) return null;
  try {
    // base64url -> base64 for atob.
    const base64 = part.replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(atob(base64)) as { sub?: unknown };
    return typeof payload.sub === 'string' ? payload.sub : null;
  } catch {
    return null;
  }
}

/**
 * True when a cross-tab access-token change means a different user is now
 * authenticated (login as another account, or logout) and the stale tab should
 * reload. False for a same-user refresh or a no-op.
 */
export function shouldReloadOnTokenChange(
  oldValue: string | null,
  newValue: string | null,
): boolean {
  return decodeSub(oldValue) !== decodeSub(newValue);
}
